// src/page/NotificationEdit.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

function NotificationEdit() {
  useAuthGuard();
  const navigate = useNavigate();
  const { notificationID } = useParams();

  const MSG = {
    apiError: 'サーバーエラーが発生しました',
    saved: '更新しました',
    deleted: '削除しました',
    errTitle: 'タイトルは必須です',
    errDelivery: '配信日時は必須です',
  };

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [desc, setDesc] = useState('');
  const [delivery, setDelivery] = useState('');
  const [openCount, setOpenCount] = useState(0);

  const [errors, setErrors] = useState({});

  const toInputLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const fromInputLocalToISO = (s) => {
    if (!s) return '';
    const d = new Date(s); // treat as local
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString();
  };

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setApiError('');
        const token = localStorage.getItem('access_token') || '';

        const res = await fetch(`${API_BASE_URL}/v1/notification/${notificationID}`, {
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          signal: ac.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const n = data?.notification || {};
        setTitle(n.notificationTitle ?? '');
        setUrl(n.notificationUrl ?? '');
        setDesc(n.notificationDescription ?? '');
        setDelivery(toInputLocal(n.deliveryDate));
        setOpenCount(Number(data?.openCount || 0));
      } catch (e) {
        if (e.name !== 'AbortError') setApiError(MSG.apiError);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [notificationID]);

  const invalid = (k) => (errors[k] ? 'is-invalid' : '');
  const feedback = (k) => errors[k] ? <div className="invalid-feedback d-block">{errors[k]}</div> : null;

  const validate = () => {
    const err = {};
    if (!title.trim()) err.title = MSG.errTitle;
    if (!delivery) err.delivery = MSG.errDelivery;
    setErrors(err);
    return err;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (Object.keys(err).length) return;

    const token = localStorage.getItem('access_token') || '';
    const dto = {
      notification: {
        notificationTitle: title,
        notificationUrl: url || undefined,
        notificationDescription: desc || undefined,
        deliveryDate: fromInputLocalToISO(delivery) || undefined,
      },
    };

    try {
      setSubmitting(true);
      setApiError('');
      const res = await fetch(`${API_BASE_URL}/v1/notification/${notificationID}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(dto),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      alert(MSG.saved);
      navigate('/notification');
    } catch (e) {
      console.error(e);
      setApiError(MSG.apiError);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('この通知を削除します。よろしいですか？')) return;
    const token = localStorage.getItem('access_token') || '';
    try {
      setDeleting(true);
      setApiError('');
      const res = await fetch(`${API_BASE_URL}/v1/notification/${notificationID}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }
      alert(MSG.deleted);
      navigate('/notification');
    } catch (e) {
      console.error(e);
      setApiError(MSG.apiError);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <nav className="mb-3" aria-label="breadcrumb">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item"><a href="/notification">プッシュ通知管理</a></li>
          <li className="breadcrumb-item active">編集</li>
        </ol>
      </nav>

      {apiError && <div className="alert alert-danger">{apiError}</div>}
      {loading && <div className="text-body-secondary">読み込み中...</div>}

      <form className="mb-9" onSubmit={handleSubmit}>
        <div className="row g-3 flex-between-end mb-5">
          <div className="col-auto">
            <h2 className="mb-2">プッシュ通知編集</h2>
          </div>
          <div className="col-auto">
            <button
              className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0"
              type="button"
              onClick={() => window.history.back()}
              disabled={submitting || deleting}
            >
              キャンセル
            </button>
            <button className="btn btn-primary me-2 mb-2 mb-sm-0" type="submit" disabled={submitting || deleting}>
              {submitting ? '更新中…' : '更新'}
            </button>
            <button
              className="btn btn-danger mb-2 mb-sm-0"
              type="button"
              onClick={handleDelete}
              disabled={submitting || deleting}
            >
              {deleting ? '削除中…' : '削除'}
            </button>
          </div>
        </div>

        <div className="row g-5">
          <div className="col-12 col-xl-8">

            {/* ID */}
            <div className="row">
              <div className="col-md-6">
                <div className="mb-4">
                  <h4 className="mb-3">通知ID</h4>
                  <input className="form-control" type="text" value={notificationID} disabled />
                </div>
              </div>
            </div>

            <div className={`mb-4 ${invalid('title')}`} data-error-key="title">
              <h4 className="mb-3">タイトル<span className="text-danger">*</span></h4>
              <input
                className={`form-control ${invalid('title')}`}
                type="text"
                name="notification_title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              {feedback('title')}
            </div>

            <div className="mb-4">
              <h4 className="mb-3">内容</h4>
              <textarea
                className="form-control"
                name="notification_description"
                rows={4}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
              />
            </div>

            <div className="mb-4">
              <h4 className="mb-3">遷移先URL<span className="text-danger">*</span></h4>
              <input
                className="form-control"
                type="text"
                name="notification_url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>

                        <div className="mb-4">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="align-middle ps-3">タップ後の画面</th>
                                        <th className="align-middle ps-3">指定するURL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>トップ画面</td>
                                        <td>（ブランク）</td>
                                    </tr>
                                    <tr>
                                        <td>イベント詳細</td>
                                        <td>/events/&#123;eventId&#125;</td>
                                    </tr>
                                    <tr>
                                        <td>スポット詳細</td>
                                        <td>/spots/&#123;spotId&#125;</td>
                                    </tr>
                                    <tr>
                                        <td>乗車券詳細</td>
                                        <td>/tickets/&#123;ticketId&#125;</td>
                                    </tr>
                                    <tr>
                                        <td>クーポン詳細</td>
                                        <td>/coupons/&#123;couponId&#125;</td>
                                    </tr>
                                    <tr>
                                        <td>お知らせ一覧</td>
                                        <td>/informations</td>
                                    </tr>
                                    <tr>
                                        <td>お知らせ詳細</td>
                                        <td>/informations/&#123;informationId&#125;</td>
                                    </tr>
                                    <tr>
                                        <td>任意のサイト</td>
                                        <td>https://www.google.com</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p className="fs-9">※）各種IDは、編集画面／詳細画面表示時のURLの最後36文字（ハイフン4文字）になります</p>
                        </div>

          </div>

          <div className="col-12 col-xl-4">
            <div className="row g-2">
              <div className="col-12 col-xl-12">
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="row gx-3">
                      <div className="col-12 col-sm-6 col-xl-12">
                        <div className={`mb-4 ${invalid('delivery')}`} data-error-key="delivery">
                          <div className="d-flex flex-wrap mb-2">
                            <h5 className="mb-0 text-body-highlight me-2">
                              配信日時<span className="text-danger">*</span>
                            </h5>
                          </div>
                          <input
                            className={`form-control ${invalid('delivery')}`}
                            type="datetime-local"
                            name="delivery_time"
                            id="delivery_time"
                            value={delivery}
                            onChange={(e) => setDelivery(e.target.value)}
                          />
                          {feedback('delivery')}
                        </div>
                      </div>
                    </div>{/* row */}

                    <div className="row gx-3">
                      <div className="col-12 col-sm-6 col-xl-12">
                        <div className={`mb-4`}>
                          <div className="d-flex flex-wrap mb-2">
                            <h5 className="mb-0 text-body-highlight me-2">
                              開封数
                            </h5>
                          </div>
                          <input
                            className={`form-control`}
                            value={openCount}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>{/* row */}

                  </div>
                </div>
              </div>
            </div>{/* row g-2 */}
          </div>
        </div>
      </form>
    </Layout>
  );
}

export default NotificationEdit;
